import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { IconeSeta } from '../../../components/Icones'
import { cn } from '../../../lib/cn'
import { TEXTOS } from '../../../lib/glossario'
import type { ComposicaoDoNumerador as Parcela } from '../../../types/chamado'
import { enderecoDoChamado } from './indicadores'

const LINK_DO_CHAMADO = 'text-sm tabular-nums text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900'

/**
 * Os chamados que formam o numerador de um indicador, abríveis um a um.
 *
 * É a diferença entre conferir e acreditar. O TRP assinado escreve "IAP = 25/36 = 69,44%"; quem
 * assina precisa poder abrir os 25 e ver que são estes. Sem a lista, o número é uma afirmação que
 * ninguém tem como contestar — e é sobre ele que se assina uma glosa de R$ 18.287,04.
 *
 * Recolhida por padrão: são até algumas dezenas de números por indicador, e três listas abertas
 * empurrariam os outros indicadores para fora da tela. Fechada, o conteúdo não existe no DOM —
 * lista escondida por CSS continua sendo alcançada pelo Tab em alguns navegadores.
 *
 * @param props as parcelas do numerador
 * @returns o bloco de composição
 */
export function ComposicaoDoNumerador({ parcelas }: { parcelas: Parcela[] }) {
  const [aberto, setAberto] = useState(false)
  const idDoPainel = useId()
  const total = parcelas.reduce((soma, parcela) => soma + parcela.idsExternos.length, 0)

  if (total === 0) {
    return null
  }

  return (
    <div className="border-t border-slate-100 pt-3">
      <button
        type="button"
        onClick={() => setAberto((valor) => !valor)}
        aria-expanded={aberto}
        aria-controls={idDoPainel}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg text-sm font-medium text-niteroi-700',
          'transition-colors hover:text-niteroi-900 focus-visible:outline focus-visible:outline-2',
          'focus-visible:outline-offset-2 focus-visible:outline-niteroi-600',
        )}
      >
        {TEXTOS.fiscalizacao.verComposicao} ({total})
        <IconeSeta className={cn('h-4 w-4 transition-transform', aberto && 'rotate-90')} />
      </button>

      {aberto && (
        <div id={idDoPainel} className="mt-3 space-y-3">
          {parcelas.map((parcela) => (
            <div key={parcela.rotulo}>
              <h4 className="text-xs font-semibold text-slate-500">
                {parcela.rotulo} ({parcela.idsExternos.length})
              </h4>
              {parcela.idsExternos.length === 0 ? (
                <p className="mt-1 text-sm text-slate-500">Nenhum.</p>
              ) : (
                <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  {parcela.idsExternos.map((idExterno) => (
                    <li key={idExterno}>
                      {/* Leva à listagem com o número buscado, e não direto ao chamado: a
                          composição traz o número do e-Niterói, que é o que o documento cita, e não
                          o identificador interno que formaria o endereço do detalhe. */}
                      <Link
                        to={enderecoDoChamado(idExterno)}
                        className={LINK_DO_CHAMADO}
                      >
                        {idExterno}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
